/**
 * useFavorites — shared hook to read/write the user's pinned modules.
 * Backed by /api/user-preferences/favorites + localStorage cache.
 *
 * Used by:
 *   - AdminSidebar  → pin star next to each module
 *   - AdminDashboard → Quick Access section
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FAV_KEY = 'bmp_dashboard_favorites_cache';

let _cache = null;
const _listeners = new Set();
const _broadcast = (next) => {
  _cache = next;
  try { localStorage.setItem(FAV_KEY, JSON.stringify(next)); } catch { /* no-op */ }
  _listeners.forEach((fn) => fn(next));
};

export const useFavorites = () => {
  const [favorites, setFavorites] = useState(() => {
    if (_cache) return _cache;
    try {
      const v = JSON.parse(localStorage.getItem(FAV_KEY) || '[]');
      _cache = v; return v;
    } catch { return []; }
  });

  useEffect(() => {
    const fn = (next) => setFavorites(next);
    _listeners.add(fn);
    // Initial server fetch (only once globally)
    if (!_cache?.__synced) {
      const token = localStorage.getItem('admin_token');
      if (token) {
        axios.get(`${API_URL}/api/user-preferences/favorites`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then((r) => {
          const next = r.data?.favorites || [];
          next.__synced = true;
          _broadcast(next);
        }).catch(() => { /* offline, keep cache */ });
      }
    }
    return () => { _listeners.delete(fn); };
  }, []);

  const persist = useCallback(async (next) => {
    _broadcast(next);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API_URL}/api/user-preferences/favorites`,
        { favorites: next.filter((x) => typeof x === 'string') },
        { headers: { Authorization: `Bearer ${token}` } });
    } catch { /* server retry on next change */ }
  }, []);

  // Optimistic local reorder (updates cache + all listeners, no server call).
  const reorder = useCallback((next) => { _broadcast(next); }, []);

  const toggle = useCallback((moduleId) => {
    const current = (_cache || []).filter((x) => typeof x === 'string');
    const next = current.includes(moduleId)
      ? current.filter((id) => id !== moduleId)
      : [...current, moduleId];
    persist(next);
  }, [persist]);

  const isFavorite = useCallback((moduleId) =>
    (favorites || []).includes(moduleId), [favorites]);

  return { favorites: (favorites || []).filter((x) => typeof x === 'string'),
           toggle, isFavorite, persist, reorder };
};

export default useFavorites;
